import React, { FC } from "react";

import { useProvidersCurrentEmployee } from "~/hooks";

import { Accordion } from "~/components/molecules";

import { SidebarLink } from "./SidebarLink";

import {
  ApiMembersUnreadNotificationCountsType,
  NavigationItemCompanyType,
  OwnerRole,
} from "~/domains";

const FocusLists: { href: string; label: string }[] = [];

const CheckOutLists = [
  { href: "/managers/employee_surveys/new", label: "新規作成" },
  { href: "/managers/employee_surveys", label: "一覧" },
];

const EmployeeConditionSurveyLists = [
  {
    href: "/managers/employee_condition_surveys/new",
    label: "新規作成",
    isOwnerOnly: true,
  },
  { href: "/managers/employee_condition_surveys", label: "一覧" },
];

const InsightObjectiveLists = [
  { href: "/managers/insight_post_objectives/new", label: "新規作成" },
  { href: "/managers/insight_post_objectives", label: "一覧" },
];

const KpiLists = [
  { href: "/managers/kpi_terms/new", label: "新規作成" },
  { href: "/managers/kpi_terms", label: "一覧" },
];

const DocumentFiles = [
  { href: "/managers/document_file_categories/new", label: "新規作成" },
  { href: "/managers/document_file_categories", label: "一覧" },
  {
    href: "/managers/document_file_category/display_orders",
    label: "並び替え",
  },
];

const RoleplayingConditionTemplateLists = [
  { href: "/managers/roleplaying_condition_templates/new", label: "新規作成" },
  { href: "/managers/roleplaying_condition_templates", label: "一覧" },
];
const RoleplayingConditions = [
  { href: "/managers/roleplaying_conditions/new", label: "新規作成" },
  { href: "/managers/roleplaying_conditions", label: "一覧" },
  {
    href: "/managers/roleplaying_condition/employees/dashboard",
    label: "状況確認",
  },
];

const MeetingNoteTemplateLists = [
  { href: "/managers/meeting_note_templates/new", label: "新規作成" },
  { href: "/managers/meeting_note_templates", label: "一覧" },
];

const MeetingNotes = [
  { href: "/managers/meeting_notes/new", label: "新規作成" },
  { href: "/managers/meeting_notes", label: "一覧" },
];

const QAndALists = [
  { href: "/managers/question_answer_categories/new", label: "新規作成" },
  { href: "/managers/question_answer_categories", label: "一覧" },
  {
    href: "/managers/question_answer_category/display_orders",
    label: "並び替え",
  },
  {
    href: "/managers/private_question_answers",
    label: "メンバーの投稿",
  },
];

const SkillCategoriesLists = [
  {
    href: "/managers/skill_evaluation_standard_summary_categories/new",
    label: "新規作成",
  },
  {
    href: "/managers/skill_evaluation_standard_summary_categories",
    label: "一覧",
  },
  {
    href: "/managers/skill_evaluation_standard_summary_category/display_orders",
    label: "並び替え",
  },
];

const SkillTermsLists = [
  {
    href: "/managers/skill_evaluation_terms/new",
    label: "新規作成",
  },
  {
    href: "/managers/skill_evaluation_terms",
    label: "一覧",
  },
];

const SalesConceptLists = [
  { href: "/managers/sales_concept_categories/new", label: "新規作成" },
  { href: "/managers/sales_concept_categories", label: "一覧" },
  {
    href: "/managers/sales_concept_category/display_orders",
    label: "並び替え",
  },
];

const UserSettingLists = [
  { href: "/managers/employees/new", label: "新規作成" },
  { href: "/managers/employees", label: "一覧" },
  { href: "/managers/left_employees", label: "無効ユーザー" },
  { href: "/managers/employee_bulk_invite", label: "一括登録" },
];

const BusinessDivisionsLists = [
  { href: "/managers/business_divisions/new", label: "新規作成" },
  { href: "/managers/business_divisions", label: "一覧" },
  { href: "/managers/archived_business_divisions", label: "無効部署" },
];

const BusinessSectionsLists = [
  { href: "/managers/business_sections/new", label: "新規作成" },
  { href: "/managers/business_sections", label: "一覧" },
  { href: "/managers/archived_business_sections", label: "無効課" },
];

const ProvidingServiceLists = [
  { href: "/managers/providing_services/new", label: "新規作成" },
  { href: "/managers/providing_services", label: "一覧" },
  { href: "/managers/archived_providing_services", label: "無効ジャンル" },
];

const JobCategoryLists = [
  { href: "/managers/job_categories/new", label: "新規作成" },
  { href: "/managers/job_categories", label: "一覧" },
  { href: "/managers/archived_job_categories", label: "無効職種" },
  {
    href: "/managers/archived_job_category_phases",
    label: "無効メモログ項目",
  },
];

type NavConfig = {
  href?: string;
  lists?: Array<{ href: string; label: string; isOwnerOnly?: boolean }>;
};

type PropsType = {
  breadCrumbText: string;
  unreadNotification?: ApiMembersUnreadNotificationCountsType;
  navigationItems?: NavigationItemCompanyType[];
};

export const ManagerSidebar: FC<PropsType> = ({
  breadCrumbText,
  unreadNotification,
  navigationItems,
}: PropsType) => {
  const { currentEmployee } = useProvidersCurrentEmployee();
  const defaultBusinessSectionId =
    currentEmployee?.businessSections[0]?.id || "";

  const navigationConfig: Record<string, NavConfig> = {
    メモログ確認: { href: "/managers/feedback/insight_posts" },
    メンバーメモ: {
      href: `/managers/business_section_reviews?business_section_id=${defaultBusinessSectionId}`,
    },
    チャレンジ: { lists: FocusLists },
    チェックアウト確認: { href: "/managers/employee_survey_submissions" },
    チェックアウト: { lists: CheckOutLists },
    コンディション確認: { href: "/managers/employee_condition_submissions" },
    コンディション: { lists: EmployeeConditionSurveyLists },
    ナレッジシェア: { href: "/managers/knowledge_post/team" },
    メモログ目標: { lists: InsightObjectiveLists },
    ライブラリ: { lists: DocumentFiles },
    スキルマップ: { lists: SkillCategoriesLists },
    スキルチェック: { lists: SkillTermsLists },
    ブログ: { lists: SalesConceptLists },
    "Q&A": { lists: QAndALists },
    "1on1・MTGテンプレ": { lists: MeetingNoteTemplateLists },
    "1on1・MTG": { lists: MeetingNotes },
    "KPI・KGI": { lists: KpiLists },
    ロールプレイングテンプレ: { lists: RoleplayingConditionTemplateLists },
    ロールプレイング: { lists: RoleplayingConditions },
    "改善・相談": { href: "/managers/feedback/kaizen_ideas" },
    AIカスタマイズ: { href: "/managers/ai_costomize/company_contexts" },
    ユーザー設定: { lists: UserSettingLists },
    部署設定: { lists: BusinessDivisionsLists },
    課設定: { lists: BusinessSectionsLists },
    ジャンル設定: { lists: ProvidingServiceLists },
    職種設定: { lists: JobCategoryLists },
    非同期Job実行状況: { href: "/managers/background_job_statuses" },
  };

  const displayNavs =
    navigationItems
      ?.filter((item) => item.role === "manager" && !item.hiddenAt)
      .sort((a, b) => a.position - b.position) || [];

  return (
    <>
      {displayNavs.map((nav, index) => {
        const navConfig = navigationConfig[nav.name];
        if (navConfig?.lists) {
          return (
            <Accordion
              key={`${nav.id}-${index}`}
              buttonClassName={`px-4 py-2 font-medium truncate ${
                breadCrumbText === nav.name
                  ? "bg-primary-600 text-white"
                  : "hover:bg-primary-300"
              }`}
              iconColor={
                breadCrumbText === nav.name
                  ? "text-white"
                  : "text-secondary-600"
              }
              title={nav.name}
              // FIXME: もっと良い書き方ありそう
              lists={navConfig.lists.filter((list) =>
                list.isOwnerOnly
                  ? currentEmployee?.employeeRole.type === OwnerRole.type
                  : true,
              )}
              isDefaultOpen={breadCrumbText === nav.name}
            />
          );
        } else if (navConfig?.href) {
          return (
            <SidebarLink
              key={`${nav.id}-${index}`}
              label={nav.name}
              href={navConfig.href}
              breadCrumbText={breadCrumbText}
              unreadNotification={unreadNotification}
            />
          );
        } else {
          return (
            <hr
              key={`${nav.id}-${index}`}
              className="bg-secondary-300 my-2 mx-4"
            />
          );
        }
      })}
    </>
  );
};
